import { increaseExplorationSpeed } from "@/game/utils/upgrades";
import useSegmentManager from "@/hooks/useSegmentManager";
import useTileRepository from "@/hooks/useTileRepository";
import type { EventCategory } from "@/types/ui";
import { FrameName } from "..";
import type HillsScoutTowerBuildingTileEntity from "../../buildings/HillsScoutTowerBuildingTileEntity";
import type TileEntity from "../../TileEntity";
import { ResourceType } from "../resources";
import { UpgradeIds } from "./ids";

export type UpgradeId = number;

export type UpgradeLevel = number;

export type PriceValue = { [K in ResourceType]?: number };

export type PriceLevels = { [level: UpgradeLevel]: PriceValue };

export * from "./ids";

export interface UpgradeItem {
	levelUnlock: number;
	heading: string | Array<string>;
	description: string;
	descriptionReplace?: { [key: string]: string };
	replaceValue: number | null; // %, number, x, a pod.
	price: PriceLevels;
	maxLevel: number;
	isStarred?: boolean;
	isTiedTo?: Connections | Array<Connections>;
	details?: string;
	eventType?: EventCategory;
	attributes?: Record<string, string | number | boolean>;
	upgradeCallback?: () => void;
}

///////////////////////////////

enum Connections {
	SETTLEMENT = "Settlement",
	GATHERERS_HUT = "Gatherer's Hut",
	EXPLORATION_SPEED = "Exploration Speed",
	LUMBER_MILL = "Lumber Mill",
	OFFLINE_INCOME = "Offline Income",
	WHEAT_FARM = "Wheat Farm",
	SHIPYARD = "Shipyard",
	FISHING_BOAT = "Fishing Boat",
	// ...
}

export const upgrades = new Map<UpgradeId, UpgradeItem>([
	[
		UpgradeIds.friendly_visits,
		{
			levelUnlock: 3,
			heading: "upgrade.friendly_visits",
			description: "upgrade.town_clicks_description",
			descriptionReplace: {
				"{percent}": "10%",
			},
			replaceValue: 0.1,
			price: {
				1: { [ResourceType.COINS]: 5_000 },
				2: { [ResourceType.COINS]: 25_000 },
				3: { [ResourceType.COINS]: 50_000 },
				4: { [ResourceType.COINS]: 75_000 },
				5: { [ResourceType.COINS]: 100_000 },
			},
			maxLevel: 5,
			isStarred: false,
			// isTiedTo: Connections.SETTLEMENT,
		},
	],
	[
		UpgradeIds.fruit_baskets,
		{
			levelUnlock: 3,
			heading: "upgrade.fruit_baskets",
			description: "upgrade.building_production_description",
			descriptionReplace: {
				"{building}": `${FrameName.GATHERERS_HUT}.name`,
				"{percent}": "10%",
			},
			replaceValue: 0.1,
			price: {
				1: { [ResourceType.COINS]: 2_000 },
				2: { [ResourceType.COINS]: 10_000 },
				3: { [ResourceType.COINS]: 50_000 },
				4: { [ResourceType.COINS]: 100_000 },
				5: { [ResourceType.COINS]: 150_000 },
			},
			maxLevel: 5,
			isStarred: false,
			upgradeCallback: (): void => {
				useSegmentManager().run(true);
			},
			// isTiedTo: Connections.GATHERERS_HUT,
		},
	],
	[
		UpgradeIds.quick_tool,
		{
			levelUnlock: 4,
			heading: "upgrade.quick_tool",
			description: "upgrade.quick_tool_description",
			replaceValue: null,
			price: {
				1: { [ResourceType.COINS]: 0 },
			},
			maxLevel: 1,
		},
	],
	[
		UpgradeIds.scout,
		{
			levelUnlock: 5,
			heading: "upgrade.scout",
			description: "upgrade.exploration_speed_description",
			descriptionReplace: {
				"{number}": "1",
			},
			replaceValue: 1,
			price: {
				1: { [ResourceType.COINS]: 10_000 },
				2: { [ResourceType.COINS]: 50_000 },
				3: { [ResourceType.COINS]: 100_000 },
				4: { [ResourceType.COINS]: 250_000 },
				5: { [ResourceType.COINS]: 500_000 },
			},
			maxLevel: 5,
			upgradeCallback: (): void => {
				increaseExplorationSpeed(1);
			},
		},
	],
	[
		UpgradeIds.bow_saw,
		{
			levelUnlock: 4,
			heading: "upgrade.bow_saw",
			description: "upgrade.lumber_mill_production_description",
			descriptionReplace: {
				"{percent}": "10%",
			},
			replaceValue: 0.1,
			price: {
				1: { [ResourceType.COINS]: 10_000, [ResourceType.GEMS]: 50 },
				2: { [ResourceType.COINS]: 50_000, [ResourceType.GEMS]: 100 },
				3: { [ResourceType.COINS]: 250_000, [ResourceType.GEMS]: 150 },
				4: {
					[ResourceType.COINS]: 500_000,
					[ResourceType.GEMS]: 200,
				},
				5: {
					[ResourceType.COINS]: 1_500_000,
					[ResourceType.GEMS]: 250,
				},
			},
			maxLevel: 5,
			// isTiedTo: Connections.LUMBER_MILL,
		},
	],
	[
		UpgradeIds.stewardship,
		{
			levelUnlock: 6,
			heading: "upgrade.stewardship",
			description: "upgrade.max_offline_time_description",
			descriptionReplace: {
				"{number}": "15",
			},
			replaceValue: 15,
			price: {
				1: {
					[ResourceType.COINS]: 150_000,
					[ResourceType.GEMS]: 350,
				},
				2: {
					[ResourceType.COINS]: 500_000,
					[ResourceType.GEMS]: 500,
				},
				3: {
					[ResourceType.COINS]: 2_500_000,
					[ResourceType.GEMS]: 1_000,
				},
				4: {
					[ResourceType.COINS]: 15_000_000,
					[ResourceType.GEMS]: 1_500,
				},
			},
			maxLevel: 4,
		},
	],
	[
		UpgradeIds.log_driving,
		{
			levelUnlock: 7,
			heading: "upgrade.log_driving",
			description: "upgrade.no_road_lumber_mills",
			replaceValue: null,
			price: {
				1: { [ResourceType.GEMS]: 250 },
			},
			maxLevel: 1,
			upgradeCallback: (): void => {},
		},
	],
	[
		UpgradeIds.horse_collar,
		{
			levelUnlock: 8,
			heading: "upgrade.horse_collar",
			description: "upgrade.building_production_description",
			descriptionReplace: {
				"{building}": `${FrameName.WHEAT_FARM}.name`,
				"{percent}": "10%",
			},
			replaceValue: 0.1,
			price: {
				1: { [ResourceType.COINS]: 50_000 },
				2: { [ResourceType.COINS]: 250_000 },
				3: { [ResourceType.COINS]: 750_000 },
				4: { [ResourceType.COINS]: 3_500_000 },
				5: { [ResourceType.COINS]: 20_000_000 },
			},
			maxLevel: 5,
			upgradeCallback: (): void => {
				useSegmentManager().run(true);
			},
		},
	],
	[
		UpgradeIds.settlers,
		{
			levelUnlock: 9,
			heading: "upgrade.settlers",
			description: "upgrade.min_new_town_level_description",
			descriptionReplace: {
				"{number}": "1",
			},
			replaceValue: 1,
			price: {
				1: { [ResourceType.COINS]: 25_000 },
				2: { [ResourceType.COINS]: 100_000 },
				3: { [ResourceType.COINS]: 200_000 },
				4: { [ResourceType.COINS]: 400_000 },
			},
			maxLevel: 4,
		},
	],
	[
		UpgradeIds.farm_roads,
		{
			levelUnlock: 10,
			heading: "upgrade.farm_roads",
			description: "upgrade.no_road_required_description",
			descriptionReplace: {
				"{building}": `${FrameName.WHEAT_FARM}.name`,
			},
			replaceValue: null,
			price: {
				1: { [ResourceType.GEMS]: 350 },
			},
			maxLevel: 1,
			upgradeCallback: (): void => {},
		},
	],
	[
		UpgradeIds.cartography,
		{
			levelUnlock: 24,
			heading: "upgrade.cartography",
			description: "upgrade.line_of_sight_description",
			descriptionReplace: {
				"{number}": "1",
			},
			replaceValue: 1,
			price: {
				1: { [ResourceType.GEMS]: 1_500 },
			},
			maxLevel: 1,
			upgradeCallback: (): void => {
				// TODO: prebehnut vsetky tile, ktore sa maju odkryt
			},
		},
	],
	[
		UpgradeIds.tax_season,
		{
			levelUnlock: 11,
			heading: "upgrade.tax_season",
			description: "upgrade.town_coin_production_description",
			descriptionReplace: {
				"{percent}": "10%",
			},
			replaceValue: 0.1,
			price: {
				1: {
					[ResourceType.COINS]: 150_000,
					[ResourceType.GEMS]: 150,
				},
				2: {
					[ResourceType.COINS]: 500_000,
					[ResourceType.GEMS]: 200,
				},
				3: {
					[ResourceType.COINS]: 2_500_000,
					[ResourceType.GEMS]: 250,
				},
				4: {
					[ResourceType.COINS]: 7_500_000,
					[ResourceType.GEMS]: 300,
				},
				5: {
					[ResourceType.COINS]: 25_000_000,
					[ResourceType.GEMS]: 350,
				},
			},
			maxLevel: 5,
		},
	],
	[
		UpgradeIds.pickaxe,
		{
			levelUnlock: 13,
			heading: "upgrade.pickaxe",
			description: "upgrade.stone_quarry_production_description",
			descriptionReplace: {
				"{percent}": "10%",
			},
			replaceValue: 0.1,
			price: {
				1: { [ResourceType.COINS]: 250_000 },
				2: { [ResourceType.COINS]: 1_000_000 },
				3: { [ResourceType.COINS]: 2_500_000 },
				4: { [ResourceType.COINS]: 7_500_000 },
				5: { [ResourceType.COINS]: 37_500_000 },
			},
			maxLevel: 5,
		},
	],
	[
		UpgradeIds.espionage,
		{
			levelUnlock: 39,
			heading: "upgrade.espionage",
			description: "upgrade.line_of_sight_description",
			descriptionReplace: {
				"{number}": "1",
			},
			replaceValue: 1,
			price: {
				1: { [ResourceType.GEMS]: 2500 },
			},
			maxLevel: 1,
		},
	],
	[
		UpgradeIds.scout_cavalry,
		{
			levelUnlock: 12,
			heading: "upgrade.scout_cavalry",
			description: "upgrade.exploration_speed_description",
			descriptionReplace: {
				"{number}": "4",
			},
			replaceValue: 4,
			price: {
				1: {
					[ResourceType.COINS]: 150_000,
					[ResourceType.GEMS]: 150,
				},
				2: {
					[ResourceType.COINS]: 500_000,
					[ResourceType.GEMS]: 200,
				},
				3: {
					[ResourceType.COINS]: 1_000_000,
					[ResourceType.GEMS]: 250,
				},
				4: {
					[ResourceType.COINS]: 2_500_000,
					[ResourceType.GEMS]: 300,
				},
				5: {
					[ResourceType.COINS]: 5_000_000,
					[ResourceType.GEMS]: 350,
				},
			},
			maxLevel: 5,
			upgradeCallback: (): void => {
				increaseExplorationSpeed(4);
			},
		},
	],
	[
		UpgradeIds.beasts_of_burden,
		{
			levelUnlock: 15,
			heading: "upgrade.beasts_of_burden",
			description: "upgrade.clay_pit_production_description",
			descriptionReplace: {
				"{percent}": "20%",
			},
			replaceValue: 0.2,
			price: {
				1: { [ResourceType.COINS]: 250_000, [ResourceType.GEMS]: 100 },
				2: { [ResourceType.COINS]: 750_000, [ResourceType.GEMS]: 150 },
				3: {
					[ResourceType.COINS]: 2_000_000,
					[ResourceType.GEMS]: 200,
				},
				4: {
					[ResourceType.COINS]: 10_000_000,
					[ResourceType.GEMS]: 250,
				},
				5: {
					[ResourceType.COINS]: 50_000_000,
					[ResourceType.GEMS]: 300,
				},
			},
			maxLevel: 5,
		},
	],
	[
		UpgradeIds.finger_on_the_pulse,
		{
			levelUnlock: 6,
			heading: "upgrade.finger_on_the_pulse",
			description: "upgrade.triple_auto_tap_description",
			replaceValue: 3,
			price: {
				1: {
					[ResourceType.GEMS]: 1_000,
				},
			},
			maxLevel: 1,
		},
	],
	[
		UpgradeIds.pro_explorer2,
		{
			levelUnlock: 18,
			heading: ["upgrade.pro_explorer", "upgrade.2.non_translatable"],
			description: "upgrade.exploration_speed_description",
			descriptionReplace: {
				"{number}": "8",
			},
			replaceValue: 8,
			price: {
				1: { [ResourceType.GEMS]: 300 },
				2: { [ResourceType.GEMS]: 400 },
				3: { [ResourceType.GEMS]: 500 },
				4: { [ResourceType.GEMS]: 600 },
				5: { [ResourceType.GEMS]: 700 },
			},
			maxLevel: 5,
			upgradeCallback: (): void => {
				increaseExplorationSpeed(8);
			},
		},
	],
	[
		UpgradeIds.bellows,
		{
			levelUnlock: 20,
			heading: "upgrade.bellows",
			description: "upgrade.building_production_description",
			descriptionReplace: {
				"{building}": `${FrameName.FURNACE}.name`,
				"{percent}": "10%",
			},
			replaceValue: 0.1,
			price: {
				1: { [ResourceType.COINS]: 500_000 },
				2: { [ResourceType.COINS]: 2_500_000 },
				3: { [ResourceType.COINS]: 10_000_000 },
				4: { [ResourceType.COINS]: 40_000_000 },
				5: { [ResourceType.COINS]: 80_000_000 },
			},
			maxLevel: 5,
		},
	],
	[
		UpgradeIds.dry_dock,
		{
			levelUnlock: 19,
			heading: "upgrade.dry_dock",
			description: "upgrade.max_fishing_boats_description",
			descriptionReplace: {
				"{number}": "1",
			},
			replaceValue: 1,
			price: {
				1: {
					[ResourceType.COINS]: 5_000_000,
					[ResourceType.GEMS]: 250,
				},
			},
			maxLevel: 1,
		},
	],
	[
		UpgradeIds.lazy_finger,
		{
			levelUnlock: 21,
			heading: "upgrade.lazy_finger",
			description: "upgrade.double_auto_tap_description",
			replaceValue: 1,
			price: {
				1: { [ResourceType.GEMS]: 1_000 },
			},
			maxLevel: 1,
		},
	],
	[
		UpgradeIds.accounting,
		{
			levelUnlock: 22,
			heading: "upgrade.accounting",
			description: "upgrade.offline_earnings_description",
			descriptionReplace: {
				"{percent}": "2%",
			},
			replaceValue: 0.02,
			price: {
				1: {
					[ResourceType.COINS]: 1_500_000,
					[ResourceType.GEMS]: 100,
				},
				2: {
					[ResourceType.COINS]: 7_500_000,
					[ResourceType.GEMS]: 150,
				},
				3: {
					[ResourceType.COINS]: 25_000_000,
					[ResourceType.GEMS]: 250,
				},
				4: {
					[ResourceType.COINS]: 75_000_000,
					[ResourceType.GEMS]: 400,
				},
				5: {
					[ResourceType.COINS]: 150_000_000,
					[ResourceType.GEMS]: 800,
				},
			},
			maxLevel: 5,
		},
	],
	[
		UpgradeIds.efficient_storage,
		{
			levelUnlock: 23,
			heading: "upgrade.efficient_storage",
			description: "upgrade.building_storage_description",
			descriptionReplace: {
				"{building}": `${FrameName.SMALL_STOCKPILE}.name`,
				"{percent}": "10%",
			},
			replaceValue: 0.1,
			price: {
				1: { [ResourceType.COINS]: 1_000_000 },
				2: { [ResourceType.COINS]: 10_000_000 },
				3: { [ResourceType.COINS]: 25_000_000 },
				4: { [ResourceType.COINS]: 50_000_000 },
				5: { [ResourceType.COINS]: 250_000_000 },
			},
			maxLevel: 5,
			upgradeCallback: (): void => {},
		},
	],
	[
		UpgradeIds.hammer_and_tongs,
		{
			levelUnlock: 27,
			heading: "upgrade.hammer_and_tongs",
			description: "upgrade.building_production_description",
			descriptionReplace: {
				"{building}": `${FrameName.BLACKSMITH}.name`,
				"{percent}": "10%",
			},
			replaceValue: 0.1,
			price: {
				1: { [ResourceType.COINS]: 250_000 },
				2: { [ResourceType.COINS]: 1_250_000 },
				3: { [ResourceType.COINS]: 5_000_000 },
				4: { [ResourceType.COINS]: 15_000_000 },
				5: { [ResourceType.COINS]: 30_000_000 },
			},
			maxLevel: 5,
		},
	],
	[
		UpgradeIds.compass,
		{
			levelUnlock: 14,
			heading: "upgrade.compass",
			description: "upgrade.exploration_discount_description",
			descriptionReplace: {
				"{percent}": "2%",
			},
			replaceValue: 0.02,
			price: {
				1: { [ResourceType.GEMS]: 250 },
				2: { [ResourceType.GEMS]: 500 },
				3: { [ResourceType.GEMS]: 750 },
				4: { [ResourceType.GEMS]: 1_000 },
				5: { [ResourceType.GEMS]: 1_250 },
			},
			maxLevel: 5,
		},
	],
	[
		UpgradeIds.smelting,
		{
			levelUnlock: 35,
			heading: "upgrade.smelting",
			description: "upgrade.foundry_production_description",
			descriptionReplace: {
				"{percent}": "10%",
			},
			replaceValue: 0.1,
			price: {
				1: {
					[ResourceType.COINS]: 500_000,
					[ResourceType.GEMS]: 100,
				},
				2: {
					[ResourceType.COINS]: 2_500_000,
					[ResourceType.GEMS]: 150,
				},
				3: {
					[ResourceType.COINS]: 15_000_000,
					[ResourceType.GEMS]: 200,
				},
				4: {
					[ResourceType.COINS]: 50_000_000,
					[ResourceType.GEMS]: 300,
				},
				5: {
					[ResourceType.COINS]: 300_000_000,
					[ResourceType.GEMS]: 500,
				},
			},
			maxLevel: 5,
		},
	],
	[
		UpgradeIds.haggling,
		{
			levelUnlock: 27,
			heading: "upgrade.haggling",
			description: "upgrade.town_upgrade_coin_discount_description",
			descriptionReplace: {
				"{percent}": "2%",
			},
			replaceValue: 0.02,
			price: {
				1: { [ResourceType.GEMS]: 200 },
				2: { [ResourceType.GEMS]: 400 },
				3: { [ResourceType.GEMS]: 700 },
				4: { [ResourceType.GEMS]: 1_000 },
				5: { [ResourceType.GEMS]: 1_250 },
			},
			maxLevel: 5,
		},
	],
	[
		UpgradeIds.efficient_packaging,
		{
			levelUnlock: 31,
			heading: "upgrade.efficient_packaging",
			description: "upgrade.building_storage_description",
			descriptionReplace: {
				"{building}": `${FrameName.WAREHOUSE}.name`,
				"{percent}": "10%",
			},
			replaceValue: 0.1,
			price: {
				1: { [ResourceType.COINS]: 1_500_000 },
				2: { [ResourceType.COINS]: 12_500_000 },
				3: { [ResourceType.COINS]: 30_000_000 },
				4: { [ResourceType.COINS]: 75_000_000 },
				5: { [ResourceType.COINS]: 375_000_000 },
			},
			maxLevel: 5,
		},
	],
	[
		UpgradeIds.fair_trade,
		{
			levelUnlock: 35,
			heading: "upgrade.fair_trade",
			description: "upgrade.town_click_as_capital_description",
			replaceValue: null,
			price: {
				1: { [ResourceType.GEMS]: 2500 },
			},
			maxLevel: 1,
		},
	],
	[
		UpgradeIds.tax_collector,
		{
			levelUnlock: 25,
			heading: "upgrade.tax_collector",
			description: "upgrade.town_click_multiplier_description",
			descriptionReplace: {
				"{number}": "1",
			},
			replaceValue: 1,
			price: {
				1: { [ResourceType.GEMS]: 300 },
				2: { [ResourceType.GEMS]: 500 },
				3: { [ResourceType.GEMS]: 750 },
				4: { [ResourceType.GEMS]: 1000 },
			},
			maxLevel: 4,
		},
	],
	[
		UpgradeIds.reclamation,
		{
			levelUnlock: 30,
			heading: "upgrade.reclamation",
			description: "upgrade.swamp_removal_description",
			replaceValue: null,
			price: {
				1: { [ResourceType.COINS]: 10_000_000 },
			},
			maxLevel: 1,
		},
	],
	[
		UpgradeIds.butterfingers,
		{
			levelUnlock: 31,
			heading: "upgrade.butterfingers",
			description: `upgrade.${ResourceType.SUPER_CLICKER}_description`,
			descriptionReplace: {
				"{number}": "2",
			},
			replaceValue: 2,
			price: {
				1: { [ResourceType.GEMS]: 150 },
				2: { [ResourceType.GEMS]: 300 },
				3: { [ResourceType.GEMS]: 450 },
				4: { [ResourceType.GEMS]: 600 },
				5: { [ResourceType.GEMS]: 750 },
			},
			maxLevel: 5,
		},
	],
	[
		UpgradeIds.scout_cavalry2,
		{
			levelUnlock: 32,
			heading: ["upgrade.scout_cavalry", "upgrade.2.non_translatable"],
			description: "upgrade.exploration_speed_description",
			descriptionReplace: {
				"{number}": "10",
			},
			replaceValue: 10,
			price: {
				1: {
					[ResourceType.COINS]: 5_000_000,
					[ResourceType.GEMS]: 300,
				},
				2: {
					[ResourceType.COINS]: 10_000_000,
					[ResourceType.GEMS]: 500,
				},
				3: {
					[ResourceType.COINS]: 50_000_000,
					[ResourceType.GEMS]: 700,
				},
				4: {
					[ResourceType.COINS]: 75_000_000,
					[ResourceType.GEMS]: 900,
				},
				5: {
					[ResourceType.COINS]: 150_000_000,
					[ResourceType.GEMS]: 1_000,
				},
			},
			maxLevel: 5,
			upgradeCallback: (): void => {
				increaseExplorationSpeed(10);
			},
		},
	],
	[
		UpgradeIds.premium_storage,
		{
			levelUnlock: 33,
			heading: "upgrade.premium_storage",
			description: "upgrade.building_storage_description",
			descriptionReplace: {
				"{building}": `${FrameName.UNIVERSAL_WAREHOUSE}.name`,
				"{percent}": "10%",
			},
			replaceValue: 0.1,
			price: {
				1: { [ResourceType.GEMS]: 200 },
				2: { [ResourceType.GEMS]: 300 },
				3: { [ResourceType.GEMS]: 400 },
				4: { [ResourceType.GEMS]: 500 },
				5: { [ResourceType.GEMS]: 600 },
			},
			maxLevel: 5,
		},
	],
	[
		UpgradeIds.treasure_hunter,
		{
			levelUnlock: 34,
			heading: "upgrade.treasure_hunter",
			description: "upgrade.chest_limit_description",
			descriptionReplace: {
				"{number}": "1",
			},
			replaceValue: 1,
			price: {
				1: { [ResourceType.COINS]: 1_000_000 },
				2: { [ResourceType.COINS]: 10_000_000 },
				3: { [ResourceType.COINS]: 100_000_000 },
			},
			maxLevel: 3,
		},
	],
	[
		UpgradeIds.tracking,
		{
			levelUnlock: 41,
			heading: "upgrade.tracking",
			description: "upgrade.building_food_production_description",
			descriptionReplace: {
				"{building}": `${FrameName.HUNTERS_LODGE}.name`,
				"{percent}": "10%",
			},
			replaceValue: 0.1,
			price: {
				1: { [ResourceType.COINS]: 1_500_000 },
				2: { [ResourceType.COINS]: 7_500_000 },
				3: { [ResourceType.COINS]: 25_000_000 },
				4: { [ResourceType.COINS]: 50_000_000 },
				5: { [ResourceType.COINS]: 100_000_000 },
			},
			maxLevel: 5,
		},
	],
	[
		UpgradeIds.architecture,
		{
			levelUnlock: 36,
			heading: "upgrade.architecture",
			description: "upgrade.town_upgrade_material_discount_description",
			descriptionReplace: {
				"{percent}": "2%",
			},
			replaceValue: 0.02,
			price: {
				1: { [ResourceType.GEMS]: 250 },
				2: { [ResourceType.GEMS]: 400 },
				3: { [ResourceType.GEMS]: 550 },
				4: { [ResourceType.GEMS]: 700 },
				5: { [ResourceType.GEMS]: 850 },
			},
			maxLevel: 5,
		},
	],
	[
		UpgradeIds.inflation,
		{
			levelUnlock: 37,
			heading: "upgrade.inflation",
			description: "upgrade.chest_content_description",
			descriptionReplace: {
				"{percent}": "20%",
			},
			replaceValue: 0.2,
			price: {
				1: { [ResourceType.COINS]: 2_000_000 },
				2: { [ResourceType.COINS]: 4_000_000 },
				3: { [ResourceType.COINS]: 8_000_000 },
				4: { [ResourceType.COINS]: 16_000_000 },
				5: { [ResourceType.COINS]: 32_000_000 },
			},
			maxLevel: 5,
		},
	],
	[
		UpgradeIds.pro_explorer3,
		{
			levelUnlock: 38,
			heading: ["upgrade.pro_explorer", "upgrade.3.non_translatable"],
			description: "upgrade.exploration_speed_description",
			descriptionReplace: {
				"{number}": "15",
			},
			replaceValue: 15,
			price: {
				1: { [ResourceType.GEMS]: 400 },
				2: { [ResourceType.GEMS]: 600 },
				3: { [ResourceType.GEMS]: 800 },
				4: { [ResourceType.GEMS]: 1_000 },
				5: { [ResourceType.GEMS]: 1_200 },
			},
			maxLevel: 5,
			upgradeCallback: (): void => {
				increaseExplorationSpeed(15);
			},
		},
	],
	[
		UpgradeIds.spyglass,
		{
			levelUnlock: 47,
			heading: "upgrade.spyglass",
			description: "upgrade.building_line_of_sight_description",
			descriptionReplace: {
				"{building}": `${FrameName.HILLS_SCOUT_TOWER}.name`,
				"{number}": "1",
			},
			replaceValue: 1,
			price: {
				1: {
					[ResourceType.COINS]: 500_000_000,
					[ResourceType.GEMS]: 300,
				},
			},
			maxLevel: 1,
			upgradeCallback: (): void => {
				const tilerepo = useTileRepository();
				tilerepo
					.getAllBuildingEntitiesByFrameNames(
						new Set<FrameName>([
							FrameName.HILLS_SCOUT_TOWER,
							FrameName.MOUNTAINS_SCOUT_TOWER,
						]),
					)
					.forEach((building: TileEntity): void => {
						(
							building as HillsScoutTowerBuildingTileEntity
						).onBuild();
					});
			},
		},
	],
	[
		UpgradeIds.miners_guild,
		{
			levelUnlock: 30,
			heading: "upgrade.miners_guild",
			description: "upgrade.building_production_description",
			descriptionReplace: {
				"{building}": `${FrameName.LARGE_STONE_QUARRY}.name`,
				"{percent}": "25%",
			},
			replaceValue: 0.25,
			price: {
				1: { [ResourceType.GEMS]: 750 },
			},
			maxLevel: 1,
		},
	],
	[
		UpgradeIds.inventory,
		{
			levelUnlock: 40,
			heading: "upgrade.inventory",
			description: "upgrade.inventory_for_buildings_description",
			replaceValue: null,
			price: {
				1: { [ResourceType.GEMS]: 1_500 },
			},
			maxLevel: 1,
			upgradeCallback: (): void => {
				// zapni inventar budov
			},
		},
	],
	[
		UpgradeIds.durable_nets,
		{
			levelUnlock: 29,
			heading: "upgrade.durable_nets",
			description: "upgrade.fishing_boat_effective_range_description",
			descriptionReplace: {
				"{number}": "1",
			},
			replaceValue: 1,
			price: {
				1: {
					[ResourceType.COINS]: 100_000_000,
					[ResourceType.GEMS]: 400,
				},
			},
			maxLevel: 1,
			upgradeCallback: (): void => {
				useSegmentManager().run(true);
			},
		},
	],
	[
		UpgradeIds.scout_cavalry3,
		{
			levelUnlock: 42,
			heading: ["upgrade.scout_cavalry", "upgrade.3.non_translatable"],
			description: "upgrade.exploration_speed_description",
			descriptionReplace: {
				"{number}": "20",
			},
			replaceValue: 20,
			price: {
				1: { [ResourceType.COINS]: 80_000_000 },
				2: { [ResourceType.COINS]: 100_000_000 },
				3: { [ResourceType.COINS]: 120_000_000 },
				4: { [ResourceType.COINS]: 140_000_000 },
				5: { [ResourceType.COINS]: 160_000_000 },
			},
			maxLevel: 5,
			upgradeCallback: (): void => {
				increaseExplorationSpeed(20);
			},
		},
	],
	[
		UpgradeIds.pro_explorer4,
		{
			levelUnlock: 43,
			heading: ["upgrade.pro_explorer", "upgrade.4.non_translatable"],
			description: "upgrade.exploration_speed_description",
			descriptionReplace: {
				"{number}": "30",
			},
			replaceValue: 30,
			price: {
				1: { [ResourceType.GEMS]: 500 },
				2: { [ResourceType.GEMS]: 1000 },
				3: { [ResourceType.GEMS]: 1_400 },
				4: { [ResourceType.GEMS]: 1_700 },
				5: { [ResourceType.GEMS]: 2_000 },
			},
			maxLevel: 5,
			upgradeCallback: (): void => {
				increaseExplorationSpeed(30);
			},
		},
	],
	[
		UpgradeIds.tents,
		{
			levelUnlock: 44,
			heading: "upgrade.tents",
			description: "upgrade.building_storage_description",
			descriptionReplace: {
				"{building}": `${FrameName.OUTPOST}.name`,
				"{percent}": "10%",
			},
			replaceValue: 0.1,
			price: {
				1: { [ResourceType.COINS]: 5_000_000 },
				2: { [ResourceType.COINS]: 15_000_000 },
				3: { [ResourceType.COINS]: 45_000_000 },
				4: { [ResourceType.COINS]: 135_000_000 },
				5: { [ResourceType.COINS]: 405_000_000 },
			},
			maxLevel: 5,
		},
	],
	[
		UpgradeIds.conscription,
		{
			levelUnlock: 46,
			heading: "upgrade.conscription",
			description: "upgrade.building_production_description",
			descriptionReplace: {
				"{building}": `${FrameName.BARRACKS}.name`,
				"{percent}": "100%",
			},
			replaceValue: 1,
			price: {
				1: { [ResourceType.GEMS]: 1_500 },
			},
			maxLevel: 1,
		},
	],
	[
		UpgradeIds.gem_rush,
		{
			levelUnlock: 46,
			heading: "upgrade.gem_rush",
			description: "upgrade.building_gem_production_description",
			descriptionReplace: {
				"{building}": `${FrameName.GEMS_MINE}.name`,
				"{number}": "1",
			},
			replaceValue: 1,
			price: {
				1: { [ResourceType.COINS]: 100_000_000 },
				2: { [ResourceType.COINS]: 1_000_000_000 },
				3: { [ResourceType.COINS]: 10_000_000_000 },
			},
			maxLevel: 3,
		},
	],
	[
		UpgradeIds.shaft_mining,
		{
			levelUnlock: 39,
			heading: "upgrade.shaft_mining",
			description: "upgrade.gold_mine_production_description",
			descriptionReplace: {
				"{percent}": "10%",
			},
			replaceValue: 0.1,
			price: {
				1: {
					[ResourceType.COINS]: 20_000_000,
					[ResourceType.GEMS]: 100,
				},
				2: {
					[ResourceType.COINS]: 40_000_000,
					[ResourceType.GEMS]: 200,
				},
				3: {
					[ResourceType.COINS]: 80_000_000,
					[ResourceType.GEMS]: 400,
				},
				4: {
					[ResourceType.COINS]: 160_000_000,
					[ResourceType.GEMS]: 800,
				},
				5: {
					[ResourceType.COINS]: 320_000_000,
					[ResourceType.GEMS]: 1_200,
				},
			},
			maxLevel: 5,
		},
	],
	[
		UpgradeIds.gold_rush,
		{
			levelUnlock: 48,
			heading: "upgrade.gold_rush",
			description: "upgrade.building_production_description",
			descriptionReplace: {
				"{building}": `${FrameName.PREMIUM_GOLD_MINE}.name`,
				"{percent}": "50%",
			},
			replaceValue: 0.5,
			price: {
				1: { [ResourceType.GEMS]: 2_000 },
			},
			maxLevel: 1,
		},
	],
	[
		UpgradeIds.craftsmanship,
		{
			levelUnlock: 49,
			heading: "upgrade.craftsmanship",
			description: "upgrade.building_production_description",
			descriptionReplace: {
				"{building}": `${FrameName.TOOLMAKER}.name`,
				"{percent}": "10%",
			},
			replaceValue: 0.1,
			price: {
				1: { [ResourceType.COINS]: 2_500_000 },
				2: { [ResourceType.COINS]: 10_000_000 },
				3: { [ResourceType.COINS]: 25_000_000 },
				4: { [ResourceType.COINS]: 50_000_000 },
				5: { [ResourceType.COINS]: 75_000_000 },
			},
			maxLevel: 5,
		},
	],
	[
		UpgradeIds.grazing,
		{
			levelUnlock: 50,
			heading: "upgrade.grazing",
			description: "upgrade.building_production_description",
			descriptionReplace: {
				"{building}": `${FrameName.LIVESTOCK_FARM}.name`,
				"{percent}": "10%",
			},
			replaceValue: 0.1,
			price: {
				1: { [ResourceType.COINS]: 2_500_000 },
				2: { [ResourceType.COINS]: 7_500_000 },
				3: { [ResourceType.COINS]: 20_000_000 },
				4: { [ResourceType.COINS]: 50_000_000 },
				5: { [ResourceType.COINS]: 200_000_000 },
			},
			maxLevel: 5,
		},
	],
	[
		UpgradeIds.coast_patrol,
		{
			levelUnlock: 21,
			heading: "upgrade.coast_patrol",
			description: "upgrade.coast_patrol_description",
			descriptionReplace: {
				"{number}": "1",
			},
			replaceValue: 1,
			price: {
				1: { [ResourceType.COINS]: 160_000 },
				2: { [ResourceType.COINS]: 800_000 },
				3: { [ResourceType.COINS]: 4_000_000 },
				4: { [ResourceType.COINS]: 20_000_000 },
				5: { [ResourceType.COINS]: 100_000_000 },
			},
			maxLevel: 5,
		},
	],
	[
		UpgradeIds.regent,
		{
			levelUnlock: 8,
			heading: "upgrade.regent",
			description: "upgrade.max_offline_time_description",
			descriptionReplace: {
				"{number}": "15",
			},
			replaceValue: 15,
			price: {
				1: { [ResourceType.GEMS]: 350 },
				2: { [ResourceType.GEMS]: 500 },
				3: { [ResourceType.GEMS]: 1_000 },
				4: { [ResourceType.GEMS]: 1_500 },
			},
			maxLevel: 4,
		},
	],
	[
		UpgradeIds.plough,
		{
			levelUnlock: 12,
			heading: "upgrade.plough",
			description: "upgrade.instant_wheat_fields_description",
			replaceValue: null,
			price: {
				1: { [ResourceType.GEMS]: 100 },
			},
			maxLevel: 1,
		},
	],
	[
		UpgradeIds.seafaring,
		{
			levelUnlock: 16,
			heading: "upgrade.seafaring",
			description: "upgrade.seafaring_description",
			descriptionReplace: {
				"{number}": "1",
			},
			replaceValue: 1,
			price: {
				1: { [ResourceType.GEMS]: 500 },
				2: { [ResourceType.GEMS]: 750 },
				3: { [ResourceType.GEMS]: 1_000 },
			},
			maxLevel: 3,
		},
	],
	[
		UpgradeIds.smooth_flour,
		{
			levelUnlock: 15,
			heading: "upgrade.smooth_flour",
			description: "upgrade.building_production_description",
			descriptionReplace: {
				"{building}": `${FrameName.BAKERY}.name`,
				"{percent}": "25%",
			},
			replaceValue: 0.25,
			price: {
				1: { [ResourceType.GEMS]: 750 },
			},
			maxLevel: 1,
		},
	],
	[
		UpgradeIds.two_man_saw,
		{
			levelUnlock: 22,
			heading: "upgrade.two_man_saw",
			description: "upgrade.building_production_description",
			descriptionReplace: {
				"{building}": `${FrameName.INDUSTRIAL_LUMBER_MILL}.name`,
				"{percent}": "25%",
			},
			replaceValue: 0.25,
			price: {
				1: { [ResourceType.GEMS]: 1_000 },
			},
			maxLevel: 1,
		},
	],
	[
		UpgradeIds.messenger,
		{
			levelUnlock: 24,
			heading: "upgrade.messenger",
			description: "upgrade.daily_reward_description",
			descriptionReplace: {
				"{percent}": "50%",
			},
			replaceValue: 0.5,
			price: {
				1: { [ResourceType.COINS]: 10_000_000 },
			},
			maxLevel: 1,
		},
	],
	[
		UpgradeIds.wooden_wedges,
		{
			levelUnlock: 26,
			heading: "upgrade.wooden_wedges",
			description: "upgrade.stone_quarry_workforce_description",
			descriptionReplace: {
				"{number}": "1",
			},
			replaceValue: 1,
			price: {
				1: {
					[ResourceType.COINS]: 5_000_000,
					[ResourceType.GEMS]: 100,
				},
				2: {
					[ResourceType.COINS]: 10_000_000,
					[ResourceType.GEMS]: 150,
				},
				3: {
					[ResourceType.COINS]: 25_000_000,
					[ResourceType.GEMS]: 250,
				},
				4: {
					[ResourceType.COINS]: 50_000_000,
					[ResourceType.GEMS]: 400,
				},
				5: {
					[ResourceType.COINS]: 100_000_000,
					[ResourceType.GEMS]: 600,
				},
			},
			maxLevel: 5,
			upgradeCallback: (): void => {
				useSegmentManager().run(true);
			},
		},
	],
	[
		UpgradeIds.millers_guild,
		{
			levelUnlock: 26,
			heading: "upgrade.millers_guild",
			description: "upgrade.no_road_mills",
			replaceValue: null,
			price: {
				1: { [ResourceType.GEMS]: 500 },
			},
			maxLevel: 1,
			upgradeCallback: (): void => {},
		},
	],
	[
		UpgradeIds.highlanders,
		{
			levelUnlock: 28,
			heading: "upgrade.highlanders",
			description: "upgrade.lumber_mills_hills_description",
			replaceValue: null,
			price: {
				1: { [ResourceType.GEMS]: 750 },
			},
			maxLevel: 1,
			upgradeCallback: (): void => {},
		},
	],
	[
		UpgradeIds.forest_paths,
		{
			levelUnlock: 29,
			heading: "upgrade.forest_paths",
			description: "upgrade.no_road_required_description",
			descriptionReplace: {
				"{building}": `${FrameName.HUNTERS_LODGE}.name`,
			},
			replaceValue: null,
			price: {
				1: { [ResourceType.GEMS]: 500 },
			},
			maxLevel: 1,
			upgradeCallback: (): void => {},
		},
	],
	[
		UpgradeIds.harbor_crane,
		{
			levelUnlock: 32,
			heading: "upgrade.harbor_crane",
			description: "upgrade.shipyard_workforce_description",
			descriptionReplace: {
				"{number}": "5",
			},
			replaceValue: 5,
			price: {
				1: { [ResourceType.GEMS]: 1_000 },
			},
			maxLevel: 1,
			upgradeCallback: (): void => {
				useSegmentManager().run(true);
			},
		},
	],
	[
		UpgradeIds.wheelbarrow,
		{
			levelUnlock: 34,
			heading: "upgrade.wheelbarrow",
			description: "upgrade.building_production_description",
			descriptionReplace: {
				"{building}": `${FrameName.LARGE_CLAY_PIT}.name`,
				"{percent}": "50%",
			},
			replaceValue: 0.5,
			price: {
				1: { [ResourceType.GEMS]: 750 },
			},
			maxLevel: 1,
		},
	],
	[
		UpgradeIds.farm_roads3,
		{
			levelUnlock: 45,
			heading: ["upgrade.farm_roads", "upgrade.3.non_translatable"],
			description: "upgrade.no_road_required_description",
			descriptionReplace: {
				"{building}": `${FrameName.BREWERY}.name`,
			},
			replaceValue: null,
			price: {
				1: { [ResourceType.GEMS]: 500 },
			},
			maxLevel: 1,
			upgradeCallback: (): void => {},
		},
	],
	[
		UpgradeIds.hoisting_winch,
		{
			levelUnlock: 47,
			heading: "upgrade.hoisting_winch",
			description: "upgrade.iron_mine_workforce_description",
			descriptionReplace: {
				"{number}": "5",
			},
			replaceValue: 5,
			price: {
				1: { [ResourceType.GEMS]: 1_500 },
			},
			maxLevel: 1,
			upgradeCallback: (): void => {
				useSegmentManager().run(true);
			},
		},
	],
	[
		UpgradeIds.pro_explorer1,
		{
			levelUnlock: 5,
			heading: "upgrade.pro_explorer",
			description: "upgrade.exploration_speed_description",
			descriptionReplace: {
				"{number}": "5",
			},
			replaceValue: 5,
			price: {
				1: { [ResourceType.GEMS]: 200 },
				2: { [ResourceType.GEMS]: 250 },
				3: { [ResourceType.GEMS]: 300 },
				4: { [ResourceType.GEMS]: 350 },
				5: { [ResourceType.GEMS]: 400 },
			},
			maxLevel: 5,
			upgradeCallback: (): void => {
				increaseExplorationSpeed(5);
			},
		},
	],
	[
		UpgradeIds.royal_earnings,
		{
			levelUnlock: 4,
			heading: "upgrade.royal_earnings",
			description: "upgrade.town_clicks_description",
			descriptionReplace: {
				"{percent}": "100%",
			},
			replaceValue: 1,
			price: {
				1: { [ResourceType.GEMS]: 800 },
			},
			maxLevel: 1,
			// isTiedTo: Connections.SETTLEMENT,
		},
	],
	[
		UpgradeIds.reeve,
		{
			levelUnlock: 17,
			heading: "upgrade.reeve",
			description: "upgrade.max_clicks_town_description",
			descriptionReplace: {
				"{percent}": "10%",
			},
			replaceValue: 0.1,
			price: {
				1: { [ResourceType.GEMS]: 150 },
				2: { [ResourceType.GEMS]: 300 },
				3: { [ResourceType.GEMS]: 450 },
				4: { [ResourceType.GEMS]: 600 },
				5: { [ResourceType.GEMS]: 750 },
			},
			maxLevel: 5,
		},
	],
	[
		UpgradeIds.scribe,
		{
			levelUnlock: 19,
			heading: "upgrade.scribe",
			description: "upgrade.tax_replenishment_description",
			descriptionReplace: {
				"{percent}": "50%",
			},
			replaceValue: 0.5,
			price: {
				1: { [ResourceType.GEMS]: 200 },
				2: { [ResourceType.GEMS]: 400 },
				3: { [ResourceType.GEMS]: 600 },
				4: { [ResourceType.GEMS]: 800 },
				5: { [ResourceType.GEMS]: 1_000 },
			},
			maxLevel: 5,
		},
	],
	[
		UpgradeIds.road_toll,
		{
			levelUnlock: 27,
			heading: "upgrade.road_toll",
			description: "upgrade.gravel_roads_income_description",
			descriptionReplace: {
				"{number}": "1",
			},
			replaceValue: 1,
			price: {
				1: {
					[ResourceType.COINS]: 25_000_000,
					[ResourceType.GEMS]: 300,
				},
			},
			maxLevel: 1,
		},
	],
	[
		UpgradeIds.accounting2,
		{
			levelUnlock: 37,
			heading: ["upgrade.accounting", "upgrade.2.non_translatable"],
			description: "upgrade.offline_earnings_description",
			descriptionReplace: {
				"{percent}": "2%",
			},
			replaceValue: 0.02,
			price: {
				1: {
					[ResourceType.COINS]: 15_000_000,
					[ResourceType.GEMS]: 300,
				},
				2: {
					[ResourceType.COINS]: 50_000_000,
					[ResourceType.GEMS]: 500,
				},
				3: {
					[ResourceType.COINS]: 150_000_000,
					[ResourceType.GEMS]: 1_000,
				},
				4: {
					[ResourceType.COINS]: 350_000_000,
					[ResourceType.GEMS]: 1_500,
				},
				5: {
					[ResourceType.COINS]: 1_250_000_000,
					[ResourceType.GEMS]: 2_500,
				},
			},
			maxLevel: 5,
		},
	],
	[
		UpgradeIds.compass2,
		{
			levelUnlock: 65,
			heading: ["upgrade.compass", "upgrade.2.non_translatable"],
			description: "upgrade.exploration_discount_description",
			descriptionReplace: {
				"{percent}": "2%",
			},
			replaceValue: 0.02,
			price: {
				1: { [ResourceType.GEMS]: 750 },
				2: { [ResourceType.GEMS]: 1_000 },
				3: { [ResourceType.GEMS]: 1_250 },
				4: { [ResourceType.GEMS]: 1_500 },
				5: { [ResourceType.GEMS]: 1_750 },
			},
			maxLevel: 5,
		},
	],
	[
		UpgradeIds.royal_advisor,
		{
			levelUnlock: 42,
			heading: "upgrade.royal_advisor",
			description: "upgrade.offline_earnings_description",
			descriptionReplace: {
				"{percent}": "5%",
			},
			replaceValue: 0.05,
			price: {
				1: { [ResourceType.GEMS]: 3_000 },
			},
			maxLevel: 1,
		},
	],
	[
		UpgradeIds.road_toll2,
		{
			levelUnlock: 43,
			heading: ["upgrade.road_toll", "upgrade.2.non_translatable"],
			description: "upgrade.cobbled_roads_income_description",
			descriptionReplace: {
				"{number}": "1",
			},
			replaceValue: 1,
			price: {
				1: {
					[ResourceType.COINS]: 20_000_000,
					[ResourceType.GEMS]: 250,
				},
				2: {
					[ResourceType.COINS]: 40_000_000,
					[ResourceType.GEMS]: 300,
				},
				3: {
					[ResourceType.COINS]: 60_000_000,
					[ResourceType.GEMS]: 350,
				},
				4: {
					[ResourceType.COINS]: 80_000_000,
					[ResourceType.GEMS]: 400,
				},
				5: {
					[ResourceType.COINS]: 100_000_000,
					[ResourceType.GEMS]: 500,
				},
			},
			maxLevel: 5,
		},
	],
	[
		UpgradeIds.forestry,
		{
			levelUnlock: 51,
			heading: "upgrade.forestry",
			description: "upgrade.automatic_forests_description",
			replaceValue: null,
			price: {
				1: { [ResourceType.GEMS]: 500 },
			},
			maxLevel: 1,
		},
	],
	[
		UpgradeIds.multi_story_accomodation,
		{
			levelUnlock: 52,
			heading: "upgrade.multi_story_accomodation",
			description: "upgrade.building_storage_description",
			descriptionReplace: {
				"{building}": `${FrameName.BARRACKS}.name`,
				"{percent}": "20%",
			},
			replaceValue: 0.2,
			price: {
				1: { [ResourceType.COINS]: 3_000_000 },
				2: { [ResourceType.COINS]: 10_000_000 },
				3: { [ResourceType.COINS]: 25_000_000 },
				4: { [ResourceType.COINS]: 50_000_000 },
				5: { [ResourceType.COINS]: 100_000_000 },
			},
			maxLevel: 5,
		},
	],
	[
		UpgradeIds.stewardship2,
		{
			levelUnlock: 53,
			heading: ["upgrade.stewardship", "upgrade.2.non_translatable"],
			description: "upgrade.max_offline_time_description",
			descriptionReplace: {
				"{number}": "15",
			},
			replaceValue: 15,
			price: {
				1: { [ResourceType.COINS]: 50_000_000 },
				2: { [ResourceType.COINS]: 150_000_000 },
				3: { [ResourceType.COINS]: 500_000_000 },
				4: { [ResourceType.COINS]: 1_500_000_000 },
			},
			maxLevel: 4,
		},
	],
	[
		UpgradeIds.regent2,
		{
			levelUnlock: 54,
			heading: ["upgrade.regent", "upgrade.2.non_translatable"],
			description: "upgrade.max_offline_time_description",
			descriptionReplace: {
				"{number}": "15",
			},
			replaceValue: 15,
			price: {
				1: { [ResourceType.GEMS]: 400 },
				2: { [ResourceType.GEMS]: 600 },
				3: { [ResourceType.GEMS]: 800 },
				4: { [ResourceType.GEMS]: 1000 },
			},
			maxLevel: 4,
		},
	],
	[
		UpgradeIds.reforestation,
		{
			levelUnlock: 55,
			heading: "upgrade.reforestation",
			description: "upgrade.old_forests_unlimited_description",
			replaceValue: null,
			price: {
				1: { [ResourceType.GEMS]: 2_500 },
			},
			maxLevel: 1,
		},
	],
	[
		UpgradeIds.scout_cavalry4,
		{
			levelUnlock: 57,
			heading: ["upgrade.scout_cavalry", "upgrade.4.non_translatable"],
			description: "upgrade.exploration_speed_description",
			descriptionReplace: {
				"{number}": "30",
			},
			replaceValue: 30,
			price: {
				1: { [ResourceType.COINS]: 200_000_000 },
				2: { [ResourceType.COINS]: 400_000_000 },
				3: { [ResourceType.COINS]: 600_000_000 },
				4: { [ResourceType.COINS]: 800_000_000 },
				5: { [ResourceType.COINS]: 1_000_000_000 },
			},
			maxLevel: 5,
			upgradeCallback: (): void => {
				increaseExplorationSpeed(30);
			},
		},
	],
	[
		UpgradeIds.pro_explorer5,
		{
			levelUnlock: 59,
			heading: ["upgrade.pro_explorer", "upgrade.5.non_translatable"],
			description: "upgrade.exploration_speed_description",
			descriptionReplace: {
				"{number}": "60",
			},
			replaceValue: 60,
			price: {
				1: { [ResourceType.GEMS]: 750 },
				2: { [ResourceType.GEMS]: 1_500 },
				3: { [ResourceType.GEMS]: 2_000 },
				4: { [ResourceType.GEMS]: 2_500 },
				5: { [ResourceType.GEMS]: 3_000 },
			},
			maxLevel: 5,
			upgradeCallback: (): void => {
				increaseExplorationSpeed(60);
			},
		},
	],
	[
		UpgradeIds.multi_story_accomodation2,
		{
			levelUnlock: 60,
			heading: [
				"upgrade.multi_story_accomodation",
				"upgrade.2.non_translatable",
			],
			description: "upgrade.building_storage_description",
			descriptionReplace: {
				"{building}": `${FrameName.ARCHERY_RANGE}.name`,
				"{percent}": "20%",
			},
			replaceValue: 0.2,
			price: {
				1: { [ResourceType.COINS]: 3_000_000 },
				2: { [ResourceType.COINS]: 10_000_000 },
				3: { [ResourceType.COINS]: 25_000_000 },
				4: { [ResourceType.COINS]: 50_000_000 },
				5: { [ResourceType.COINS]: 100_000_000 },
			},
			maxLevel: 5,
		},
	],
	[
		UpgradeIds.trade_cog,
		{
			levelUnlock: 61,
			heading: "upgrade.trade_cog",
			description: "upgrade.effective_range_description",
			descriptionReplace: {
				"{building}": `${FrameName.PORT}.name`,
				"{number}": "1",
			},
			replaceValue: 1,
			price: {
				1: { [ResourceType.GEMS]: 1_000 },
			},
			maxLevel: 1,
		},
	],
	[
		UpgradeIds.wine_cellar,
		{
			levelUnlock: 62,
			heading: "upgrade.wine_cellar",
			description: "upgrade.building_production_description",
			descriptionReplace: {
				"{building}": `${FrameName.INN}.name`,
				"{percent}": "50%",
			},
			replaceValue: 0.5,
			price: {
				1: { [ResourceType.GEMS]: 2_500 },
			},
			maxLevel: 1,
		},
	],
	[
		UpgradeIds.conscription2,
		{
			levelUnlock: 63,
			heading: ["upgrade.conscription", "upgrade.2.non_translatable"],
			description: "upgrade.building_production_description",
			descriptionReplace: {
				"{building}": `${FrameName.ARCHERY_RANGE}.name`,
				"{percent}": "50%",
			},
			replaceValue: 0.5,
			price: {
				1: { [ResourceType.GEMS]: 1_500 },
			},
			maxLevel: 1,
		},
	],
	[
		UpgradeIds.exotic_goods,
		{
			levelUnlock: 63,
			heading: "upgrade.exotic_goods",
			description: "upgrade.building_production_description",
			descriptionReplace: {
				"{building}": `${FrameName.MARKET}.name`,
				"{percent}": "10%",
			},
			replaceValue: 0.1,
			price: {
				1: {
					[ResourceType.COINS]: 25_000_000,
					[ResourceType.GEMS]: 50,
				},
				2: {
					[ResourceType.COINS]: 50_000_000,
					[ResourceType.GEMS]: 75,
				},
				3: {
					[ResourceType.COINS]: 75_000_000,
					[ResourceType.GEMS]: 100,
				},
				4: {
					[ResourceType.COINS]: 100_000_000,
					[ResourceType.GEMS]: 125,
				},
				5: {
					[ResourceType.COINS]: 125_000_000,
					[ResourceType.GEMS]: 150,
				},
			},
			maxLevel: 5,
		},
	],
	[
		UpgradeIds.free_market,
		{
			levelUnlock: 64,
			heading: "upgrade.free_market",
			description: "upgrade.building_consumption_description",
			descriptionReplace: {
				"{building}": `${FrameName.MARKET}.name`,
				"{percent}": "100%",
			},
			replaceValue: 1,
			price: {
				1: { [ResourceType.GEMS]: 750 },
			},
			maxLevel: 1,
		},
	],
	[
		UpgradeIds.ingots,
		{
			levelUnlock: 64,
			heading: "upgrade.ingots",
			description: "upgrade.foundry_consumption_description",
			descriptionReplace: {
				"{building1}": `${FrameName.FOUNDRY}.name`,
				"{building2}": `${FrameName.INDUSTRIAL_FOUNDRY}.name`,
				"{percent}": "10%",
			},
			replaceValue: 0.1,
			price: {
				1: {
					[ResourceType.COINS]: 100_000_000,
					[ResourceType.GEMS]: 150,
				},
				2: {
					[ResourceType.COINS]: 200_000_000,
					[ResourceType.GEMS]: 300,
				},
				3: {
					[ResourceType.COINS]: 300_000_000,
					[ResourceType.GEMS]: 450,
				},
				4: {
					[ResourceType.COINS]: 400_000_000,
					[ResourceType.GEMS]: 600,
				},
				5: {
					[ResourceType.COINS]: 500_000_000,
					[ResourceType.GEMS]: 750,
				},
			},
			maxLevel: 5,
		},
	],
	[
		UpgradeIds.naval_trade,
		{
			levelUnlock: 66,
			heading: "upgrade.naval_trade",
			description: "upgrade.naval_trade_description",
			descriptionReplace: {
				"{number}": "1",
			},
			replaceValue: 1,
			price: {
				1: { [ResourceType.GEMS]: 5_000 },
			},
			maxLevel: 1,
		},
	],
	[
		UpgradeIds.stewardship3,
		{
			levelUnlock: 68,
			heading: ["upgrade.stewardship", "upgrade.3.non_translatable"],
			description: "upgrade.max_offline_time_description",
			descriptionReplace: {
				"{number}": "15",
			},
			replaceValue: 15,
			price: {
				1: { [ResourceType.COINS]: 1_000_000_000 },
				2: { [ResourceType.COINS]: 2_000_000_000 },
				3: { [ResourceType.COINS]: 3_000_000_000 },
				4: { [ResourceType.COINS]: 5_000_000_000 },
			},
			maxLevel: 4,
		},
	],
	[
		UpgradeIds.regent3,
		{
			levelUnlock: 70,
			heading: ["upgrade.regent", "upgrade.3.non_translatable"],
			description: "upgrade.max_offline_time_description",
			descriptionReplace: {
				"{number}": "15",
			},
			replaceValue: 15,
			price: {
				1: { [ResourceType.GEMS]: 600 },
				2: { [ResourceType.GEMS]: 900 },
				3: { [ResourceType.GEMS]: 1_200 },
				4: { [ResourceType.GEMS]: 1_500 },
			},
			maxLevel: 4,
		},
	],
	[
		UpgradeIds.snorkel,
		{
			levelUnlock: 71,
			heading: "upgrade.snorkel",
			description: "upgrade.building_production_description",
			descriptionReplace: {
				"{building}": `${FrameName.SHALLOW_SEA_PEARL_HUNTER}.name`,
				"{percent}": "10%",
			},
			replaceValue: 0.1,
			price: {
				1: { [ResourceType.COINS]: 400_000_000 },
				2: { [ResourceType.COINS]: 600_000_000 },
				3: { [ResourceType.COINS]: 800_000_000 },
				4: { [ResourceType.COINS]: 1_000_000_000 },
				5: { [ResourceType.COINS]: 2_000_000_000 },
			},
			maxLevel: 5,
		},
	],
	[
		UpgradeIds.mountain_lumberjacks,
		{
			levelUnlock: 72,
			heading: "upgrade.mountain_lumberjacks",
			description: "upgrade.mountain_lumberjacks_common_description",
			replaceValue: null,
			price: {
				1: { [ResourceType.COINS]: 10_000_000_000 },
			},
			maxLevel: 1,
		},
	],
	[
		UpgradeIds.efficient_storage2,
		{
			levelUnlock: 74,
			heading: [
				"upgrade.efficient_storage",
				"upgrade.2.non_translatable",
			],
			description: "upgrade.building_storage_description",
			descriptionReplace: {
				"{building}": `${FrameName.PORT}.name`,
				"{percent}": "10%",
			},
			replaceValue: 0.1,
			price: {
				1: {
					[ResourceType.COINS]: 300_000_000,
					[ResourceType.GEMS]: 100,
				},
				2: {
					[ResourceType.COINS]: 600_000_000,
					[ResourceType.GEMS]: 200,
				},
				3: {
					[ResourceType.COINS]: 1_200_000_000,
					[ResourceType.GEMS]: 300,
				},
				4: {
					[ResourceType.COINS]: 2_500_000_000,
					[ResourceType.GEMS]: 500,
				},
				5: {
					[ResourceType.COINS]: 5_000_000_000,
					[ResourceType.GEMS]: 750,
				},
			},
			maxLevel: 5,
		},
	],
	[
		UpgradeIds.scout2,
		{
			levelUnlock: 26,
			heading: ["upgrade.scout", "upgrade.2.non_translatable"],
			description: "upgrade.exploration_speed_description",
			descriptionReplace: {
				"{number}": "9",
			},
			replaceValue: 9,
			price: {
				1: {
					[ResourceType.COINS]: 3_500_000,
					[ResourceType.GEMS]: 300,
				},
				2: {
					[ResourceType.COINS]: 7_000_000,
					[ResourceType.GEMS]: 400,
				},
				3: {
					[ResourceType.COINS]: 20_000_000,
					[ResourceType.GEMS]: 500,
				},
				4: {
					[ResourceType.COINS]: 35_000_000,
					[ResourceType.GEMS]: 600,
				},
				5: {
					[ResourceType.COINS]: 75_000_000,
					[ResourceType.GEMS]: 700,
				},
			},
			maxLevel: 5,
			upgradeCallback: (): void => {
				increaseExplorationSpeed(9);
			},
		},
	],
	[
		UpgradeIds.beasts_of_burden2,
		{
			levelUnlock: 40,
			heading: ["upgrade.beasts_of_burden", "upgrade.2.non_translatable"],
			description: "upgrade.clay_pit_production_description",
			descriptionReplace: {
				"{percent}": "20%",
			},
			replaceValue: 0.2,
			price: {
				1: {
					[ResourceType.COINS]: 2_500_000,
					[ResourceType.GEMS]: 100,
				},
				2: {
					[ResourceType.COINS]: 10_000_000,
					[ResourceType.GEMS]: 150,
				},
				3: {
					[ResourceType.COINS]: 30_000_000,
					[ResourceType.GEMS]: 200,
				},
				4: {
					[ResourceType.COINS]: 80_000_000,
					[ResourceType.GEMS]: 250,
				},
				5: {
					[ResourceType.COINS]: 150_000_000,
					[ResourceType.GEMS]: 300,
				},
			},
			maxLevel: 5,
		},
	],
	[
		UpgradeIds.farm_roads2,
		{
			levelUnlock: 41,
			heading: ["upgrade.farm_roads", "upgrade.2.non_translatable"],
			description: "upgrade.no_road_required_description",
			descriptionReplace: {
				"{building}": `${FrameName.LIVESTOCK_FARM}.name`,
			},
			replaceValue: null,
			price: {
				1: { [ResourceType.GEMS]: 500 },
			},
			maxLevel: 1,
			upgradeCallback: (): void => {},
		},
	],
	[
		UpgradeIds.harpoon,
		{
			levelUnlock: 44,
			heading: "upgrade.harpoon",
			description: "upgrade.big_fish_production_description",
			descriptionReplace: {
				"{percent}": "20%",
			},
			replaceValue: 0.2,
			price: {
				1: { [ResourceType.GEMS]: 500 },
			},
			maxLevel: 1,
		},
	],
	[
		UpgradeIds.attic3,
		{
			levelUnlock: 48,
			heading: ["upgrade.attic", "upgrade.3.non_translatable"],
			description: "upgrade.big_house_workforce_description",
			descriptionReplace: {
				"{number}": "5",
			},
			replaceValue: 5,
			price: {
				1: { [ResourceType.COINS]: 25_000_000 },
				2: { [ResourceType.COINS]: 100_000_000 },
				3: { [ResourceType.COINS]: 250_000_000 },
				4: { [ResourceType.COINS]: 500_000_000 },
				5: { [ResourceType.COINS]: 750_000_000 },
			},
			maxLevel: 5,
		},
	],
	[
		UpgradeIds.scout3,
		{
			levelUnlock: 51,
			heading: ["upgrade.scout", "upgrade.3.non_translatable"],
			description: "upgrade.exploration_speed_description",
			descriptionReplace: {
				"{number}": "25",
			},
			replaceValue: 25,
			price: {
				1: {
					[ResourceType.COINS]: 150_000_000,
					[ResourceType.GEMS]: 400,
				},
				2: {
					[ResourceType.COINS]: 300_000_000,
					[ResourceType.GEMS]: 800,
				},
				3: {
					[ResourceType.COINS]: 450_000_000,
					[ResourceType.GEMS]: 1_200,
				},
				4: {
					[ResourceType.COINS]: 600_000_000,
					[ResourceType.GEMS]: 1_500,
				},
				5: {
					[ResourceType.COINS]: 750_000_000,
					[ResourceType.GEMS]: 1_800,
				},
			},
			maxLevel: 5,
			upgradeCallback: (): void => {
				increaseExplorationSpeed(25);
			},
		},
	],
	[
		UpgradeIds.mountain_paths,
		{
			levelUnlock: 53,
			heading: "upgrade.mountain_paths",
			description: "upgrade.no_road_required_description",
			descriptionReplace: {
				"{building}": `${FrameName.MOUNTAIN_BELLOWS}.name`,
			},
			replaceValue: null,
			price: {
				1: { [ResourceType.GEMS]: 500 },
			},
			maxLevel: 1,
			upgradeCallback: (): void => {},
		},
	],
	[
		UpgradeIds.outskirts,
		{
			levelUnlock: 25,
			heading: "upgrade.outskirts",
			description: "upgrade.small_house_outskirts_description",
			descriptionReplace: {
				"{number}": "1",
			},
			replaceValue: null,
			price: {
				1: {
					[ResourceType.COINS]: 30_000_000,
					[ResourceType.GEMS]: 1_000,
				},
			},
			maxLevel: 1,
			upgradeCallback: (): void => {},
		},
	],
	[
		UpgradeIds.gold_panning,
		{
			levelUnlock: 65,
			heading: "upgrade.gold_panning",
			description: "upgrade.no_road_required_description",
			descriptionReplace: {
				"{building}": `${FrameName.GOLD_PROSPECTOR}.name`,
			},
			replaceValue: null,
			price: {
				1: { [ResourceType.GEMS]: 500 },
			},
			maxLevel: 1,
			upgradeCallback: (): void => {},
		},
	],
	[
		UpgradeIds.attic,
		{
			levelUnlock: 16,
			heading: "upgrade.attic",
			description: "upgrade.small_house_workforce_description",
			descriptionReplace: {
				"{number}": "2",
			},
			replaceValue: 2,
			price: {
				1: { [ResourceType.COINS]: 300_000 },
				2: { [ResourceType.COINS]: 1_200_000 },
				3: { [ResourceType.COINS]: 3_500_000 },
				4: { [ResourceType.COINS]: 7_500_000 },
				5: { [ResourceType.COINS]: 15_000_000 },
			},
			maxLevel: 5,
		},
	],
	[
		UpgradeIds.sanitation_system,
		{
			levelUnlock: 20,
			heading: "upgrade.sanitation_system",
			description: "upgrade.small_house_workforce_description",
			descriptionReplace: {
				"{number}": "10",
			},
			replaceValue: 10,
			price: {
				1: { [ResourceType.GEMS]: 500 },
			},
			maxLevel: 1,
		},
	],
	[
		UpgradeIds.attic2,
		{
			levelUnlock: 23,
			heading: ["upgrade.attic", "upgrade.2.non_translatable"],
			description: "upgrade.medium_house_workforce_description",
			descriptionReplace: {
				"{number}": "3",
			},
			replaceValue: 3,
			price: {
				1: { [ResourceType.COINS]: 1_500_000 },
				2: { [ResourceType.COINS]: 3_000_000 },
				3: { [ResourceType.COINS]: 6_000_000 },
				4: { [ResourceType.COINS]: 12_000_000 },
				5: { [ResourceType.COINS]: 24_000_000 },
			},
			maxLevel: 5,
		},
	],
	[
		UpgradeIds.sanitation_system2,
		{
			levelUnlock: 33,
			heading: [
				"upgrade.sanitation_system",
				"upgrade.2.non_translatable",
			],
			description: "upgrade.medium_house_workforce_description",
			descriptionReplace: {
				"{number}": "15",
			},
			replaceValue: 15,
			price: {
				1: { [ResourceType.GEMS]: 1_500 },
			},
			maxLevel: 1,
		},
	],
	[
		UpgradeIds.outskirts2,
		{
			levelUnlock: 38,
			heading: ["upgrade.outskirts", "upgrade.2.non_translatable"],
			description: "upgrade.medium_house_outskirts_description",
			descriptionReplace: {
				"{number}": "1",
			},
			replaceValue: null,
			price: {
				1: {
					[ResourceType.COINS]: 200_000_000,
					[ResourceType.GEMS]: 2_000,
				},
			},
			maxLevel: 1,
			upgradeCallback: (): void => {},
		},
	],
	[
		UpgradeIds.town_privileges,
		{
			levelUnlock: 45,
			heading: "upgrade.town_privileges",
			description: "upgrade.town_workforce_description",
			descriptionReplace: {
				"{percent}": "10%",
			},
			replaceValue: 0.1,
			price: {
				1: {
					[ResourceType.GEMS]: 3_000,
				},
			},
			maxLevel: 1,
		},
	],
	[
		UpgradeIds.sanitation_system3,
		{
			levelUnlock: 52,
			heading: [
				"upgrade.sanitation_system",
				"upgrade.3.non_translatable",
			],
			description: "upgrade.big_house_workforce_description",
			descriptionReplace: {
				"{number}": "25",
			},
			replaceValue: 25,
			price: {
				1: { [ResourceType.GEMS]: 3_000 },
			},
			maxLevel: 1,
		},
	],
	[
		UpgradeIds.outskirts3,
		{
			levelUnlock: 58,
			heading: ["upgrade.outskirts", "upgrade.3.non_translatable"],
			description: "upgrade.big_house_outskirts_description",
			descriptionReplace: {
				"{number}": "1",
			},
			replaceValue: null,
			price: {
				1: {
					[ResourceType.GEMS]: 4_000,
				},
			},
			maxLevel: 1,
			upgradeCallback: (): void => {},
		},
	],
	[
		UpgradeIds.town_privileges2,
		{
			levelUnlock: 60,
			heading: ["upgrade.town_privileges", "upgrade.2.non_translatable"],
			description: "upgrade.town_workforce_description",
			descriptionReplace: {
				"{percent}": "15%",
			},
			replaceValue: 0.15,
			price: {
				1: {
					[ResourceType.GEMS]: 5_000,
				},
			},
			maxLevel: 1,
		},
	],
	[
		UpgradeIds.town_privileges3,
		{
			levelUnlock: 75,
			heading: ["upgrade.town_privileges", "upgrade.3.non_translatable"],
			description: "upgrade.town_workforce_description",
			descriptionReplace: {
				"{percent}": "25%",
			},
			replaceValue: 0.25,
			price: {
				1: {
					[ResourceType.GEMS]: 10_000,
				},
			},
			maxLevel: 1,
		},
	],

	/********* POINT OF INTEREST ********/
	[
		UpgradeIds.embankment,
		{
			levelUnlock: 0,
			heading: "upgrade.embankment",
			description: "ui.swamp_roads",
			replaceValue: 0,
			price: {},
			maxLevel: 1,
		},
	],
	[
		UpgradeIds.max_offline_time,
		{
			levelUnlock: 0,
			heading: "upgrade.hourglass",
			description: "upgrade.max_offline_time_description",
			descriptionReplace: {
				"{number}": "60",
			},
			replaceValue: 60,
			price: {},
			maxLevel: 1,
		},
	],
	[
		UpgradeIds.line_of_sight,
		{
			levelUnlock: 0,
			heading: "upgrade.telescope",
			description: "upgrade.building_line_of_sight_description",
			descriptionReplace: {
				"{building}": `${FrameName.HILLS_SCOUT_TOWER}.name`,
			},
			replaceValue: 1,
			price: {},
			maxLevel: 1,
		},
	],
	[
		UpgradeIds.mushroom,
		{
			levelUnlock: 0,
			heading: "poi.mushroom",
			description: "upgrade.building_production_description",
			descriptionReplace: {
				"{building}": `${FrameName.UNIVERSITY}.name`,
				"{percent}": "40%",
			},
			replaceValue: 0.4,
			price: {},
			maxLevel: 1,
			upgradeCallback: (): void => {
				useSegmentManager().run(true);
			},
		},
	],
	[
		UpgradeIds.pirate_navigation,
		{
			levelUnlock: 0,
			heading: "poi.pirate_harbour",
			description: "ui.sea_exploration_discount",
			descriptionReplace: {
				"{percent}": "15%",
			},
			replaceValue: 0.15,
			price: {},
			maxLevel: 1,
		},
	],
	[
		UpgradeIds.atol,
		{
			levelUnlock: 0,
			heading: "poi.atol",
			description: "upgrade.building_production_description",
			descriptionReplace: {
				"{building}": `${FrameName.SHALLOW_SEA_PEARL_HUNTER}.name`,
				"{percent}": "25%",
			},
			replaceValue: 0.25,
			price: {},
			maxLevel: 1,
			upgradeCallback: (): void => {
				useSegmentManager().run(true);
			},
		},
	],
	[
		UpgradeIds.crater,
		{
			levelUnlock: 0,
			heading: "poi.crater",
			description: "upgrade.building_production_description",
			descriptionReplace: {
				"{building}": `${FrameName.UNIVERSITY}.name`,
				"{percent}": "60%",
			},
			replaceValue: 0.6,
			price: {},
			maxLevel: 1,
			upgradeCallback: (): void => {
				useSegmentManager().run(true);
			},
		},
	],
	[
		UpgradeIds.kraken,
		{
			levelUnlock: 0,
			heading: "poi.kraken",
			description: "upgrade.min_new_town_level_description",
			descriptionReplace: {
				"{number}": "10",
			},
			replaceValue: 10,
			price: {},
			maxLevel: 1,
		},
	],
	[
		UpgradeIds.witch_lake,
		{
			levelUnlock: 0,
			heading: "poi.witch_lake",
			description: "ui.daily_triple_income_limit_increase",
			descriptionReplace: {
				"{number}": "2",
			},
			replaceValue: 2,
			price: {},
			maxLevel: 1,
		},
	],
	[
		UpgradeIds.geyser,
		{
			levelUnlock: 0,
			heading: "poi.geyser",
			description: "upgrade.chest_content_description",
			descriptionReplace: {
				"{percent}": "25%",
			},
			replaceValue: 0.25,
			price: {},
			maxLevel: 1,
		},
	],
]);

export const getNewUpgradesFromLevel = (
	level: number,
): Map<UpgradeId, UpgradeItem> => {
	const newUpgrades = new Map<UpgradeId, UpgradeItem>();
	for (const [id, upgrade] of upgrades) {
		if (upgrade.levelUnlock === level) {
			newUpgrades.set(id, upgrade);
		}
	}
	return newUpgrades;
};

export const getNewUpgradesIdsFromLevel = (level: number): Array<UpgradeId> => {
	const newUpgradesIds: Array<UpgradeId> = [];
	for (const [id, upgrade] of upgrades) {
		if (upgrade.levelUnlock === level) {
			newUpgradesIds.push(id);
		}
	}
	return newUpgradesIds;
};
